/**
 * Racine de l'application — les routes sont DÉRIVÉES du registre.
 * Aucune route de module en dur ici (verrouillé par le test d'invariants).
 */
import type { Role } from "@geceram/shared";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SessionProvider, useSession } from "./auth/session";
import { Bulle } from "./aide/Bulle";
import { Icon, IconSprite } from "./icons";
import { registry, routesFor, routesPubliques } from "./registry";
import { avecDelai, destinationAccueil, modulesAccueil } from "./accueil";
import { Shell } from "./Shell";
import { LoadingState, EmptyState } from "./ux/states";
import { usePointerEventsGuard } from "./ux/usePointerEventsGuard";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 1 } },
});

function LoginScreen() {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--surface-page)" }}>
      <div className="card" style={{ padding: 40, textAlign: "center", maxWidth: 420 }}>
        {/* Le logo porte deja le nom « GECERAM » ; le titre ne garde donc que
            ce qu'il ajoute — qu'il s'agit du CRM. */}
        <img className="logo-gc-clair" src="/logos/geceram.png" alt="GÉCÉRAM" />
        <h1 style={{ margin: "0 0 6px", fontSize: 22, lineHeight: "30px" }}>CRM</h1>
        <p style={{ margin: "0 0 22px", color: "var(--text-secondary)", fontSize: 14.5 }}>
          Réservé à l'équipe du laboratoire.
        </p>
        <a className="btn pri full" href="/api/v1/auth/login">
          <Icon id="i-lock" size="sm" />
          Continuer avec Google
        </a>
      </div>
    </div>
  );
}

/**
 * L'ACCUEIL — et le module qui a le droit de le capter (C13, R5).
 *
 * Par défaut : la première route MONTÉE pour ce rôle. (Un module restreint en
 * tête de registre envoyait les autres rôles sur une route inexistante —
 * « Page introuvable ».)
 *
 * Un module peut demander à passer devant en déclarant `accueil` : le noyau
 * pose SA question et ouvre le chemin qu'elle rend. Rien n'est nommé ici — ni
 * route, ni module, ni événement d'audit (règle D8).
 *
 * ⚠ LES HOOKS SONT APPELÉS SANS CONDITION. `useQuery` tourne à chaque rendu,
 * même quand aucun module ne capte : c'est `enabled` qui décide de poser la
 * question, pas un `if` autour du hook. La décision, elle, est déportée dans
 * `destinationAccueil` — une fonction pure, donc testable sans rendu, et c'est
 * là que vit la règle « une erreur ne ferme jamais la porte ».
 */
function Home({ roles }: { roles: readonly Role[] }) {
  const premiereRoute = routesFor(roles)[0]?.path ?? null;
  const premier = modulesAccueil(registry, roles)[0];
  // `capte` en constante : la question du module est ainsi BORNÉE par le noyau
  // (`avecDelai`) — une `queryFn` muette échoue au bout de 5 s au lieu de
  // laisser « Ouverture… » à l'écran pour toujours. Le rejet repart dans le
  // chemin d'erreur déjà prévu : accueil normal.
  const capte = premier?.accueil;
  const q = useQuery({
    queryKey: capte ? [...capte.queryKey] : ["accueil", "aucun"],
    queryFn: capte ? () => avecDelai(capte.queryFn()) : async () => null,
    enabled: Boolean(premier),
    // Une seule lecture par session : sans `staleTime: Infinity`, chaque retour
    // sur l'accueil rejouerait la question. `retry: false` : en cas de panne on
    // entre tout de suite, on ne fait pas patienter trois fois pour rien.
    staleTime: Infinity,
    retry: false,
  });

  const decision = premier
    ? destinationAccueil(q.data, q.isError, premiereRoute)
    : { attendre: false, vers: premiereRoute };

  if (decision.attendre) return <LoadingState label="Ouverture…" />;
  if (decision.vers) return <Navigate to={decision.vers} replace />;
  return (
    <EmptyState
      title="Aucun module installé"
      hint="Déposer un dossier dans src/modules/ — le registre le découvrira au prochain build."
    />
  );
}

/**
 * LES PAGES SERVIES HORS SESSION — étape É8.
 *
 * ⚠ ELLES VIVENT AU-DESSUS DE LA SESSION, ET C'EST TOUT LEUR INTÉRÊT.
 * `AuthedApp` rend l'écran de connexion dès que la session est anonyme ; un
 * cabinet n'a pas de compte — c'est l'objet même du lien public. Montées à
 * l'intérieur, elles auraient affiché un formulaire de connexion sur un lien
 * que le laboratoire venait d'envoyer.
 *
 * ⚠ RIEN N'EST NOMMÉ ICI. Comme toutes les autres, ces routes sont DÉRIVÉES du
 * registre : le noyau ne sait pas quel module les fournit. Ma première version
 * importait la page du module directement — la règle D8 du cahier des charges
 * l'a refusée, et elle avait raison.
 */
function AuthedApp() {
  const session = useSession();
  if (session.status === "loading") return <LoadingState label="Ouverture de session…" />;
  if (session.status === "anonymous") return <LoginScreen />;

  const { me, devStub } = session;
  return (
    <Routes>
      <Route element={<Shell me={me} devStub={devStub} />}>
        <Route index element={<Home roles={me.roles} />} />
        {routesFor(me.roles).map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              <Suspense fallback={<LoadingState />}>
                <r.component />
              </Suspense>
            }
          />
        ))}
        <Route
          path="*"
          element={<EmptyState title="Page introuvable" hint="Le lien est peut-être périmé." />}
        />
      </Route>
    </Routes>
  );
}

export function App() {
  usePointerEventsGuard();
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <IconSprite />
        <Bulle />
        {/*
          LA ZONE DES NOTIFICATIONS — sans elle, tout message est avalé.
          Le module Partage signale TOUT par ce canal : « Cas créé », « Fichier
          déposé », et surtout les refus d'écriture. Le CRM n'en avait pas
          besoin avant son arrivée, donc personne ne l'avait montée.
          Mesuré avant correction : cliquer « Finaliser » ne produisait
          strictement rien — ni message, ni trace. Un échec silencieux est pire
          qu'un échec bruyant : on recommence, on doute de son geste, et on ne
          signale jamais le vrai problème.
        */}
        <Toaster position="bottom-right" richColors closeButton />
        <BrowserRouter>
          <Routes>
            {routesPubliques().map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  <Suspense fallback={<LoadingState label="Ouverture du dossier…" />}>
                    <r.component />
                  </Suspense>
                }
              />
            ))}
            {/* Tout le reste du CRM reste derrière la session. */}
            <Route path="*" element={<AuthedApp />} />
          </Routes>
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  );
}
